/**
 * Editions Milano theme scripts. Progressive enhancement only: every page
 * works without JavaScript (forms post normally, menu is <details>).
 */

/**
 * Quantity stepper: + 1 – (EM_Web_03 p. 23), never below 1. With
 * data-autosubmit (cart page) each change saves the cart: steps in quick
 * succession are sent together.
 */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener('click', () => this.step(Number(button.dataset.step)));
    });
    this.input.addEventListener('change', () => this.step(0));
  }

  step(delta) {
    const before = this.input.value;
    this.input.value = String(Math.max(1, (parseInt(this.input.value, 10) || 1) + delta));
    if (!this.hasAttribute('data-autosubmit') || (delta !== 0 && this.input.value === before)) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.closest('form')?.requestSubmit(), 600);
  }
}

/**
 * Save in wishlist: remembers product handles in this browser only (no
 * account); the Wishlist page lists them. The button shows its saved state.
 */
const WISHLIST_KEY = 'editions-milano:wishlist';

function readWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
  } catch {
    return [];
  }
}

/**
 * Product page on phones (EM_Web_WIP_04 pp. 42–45): "More info" unfolds
 * the specs and becomes "Less info". Without JavaScript the button stays
 * hidden and the specs show.
 */
function setupMoreInfo() {
  const button = document.querySelector('[data-more-toggle]');
  if (!button) return;
  const more = button.textContent;
  button.hidden = false;
  button.addEventListener('click', () => {
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    button.textContent = open ? button.dataset.labelLess : more;
  });
}

function setupWishlist() {
  document.querySelectorAll('[data-wishlist]').forEach((button) => {
    const handle = button.dataset.wishlist;
    const labelAdd = button.textContent;
    const render = (saved) => {
      button.setAttribute('aria-pressed', String(saved));
      button.textContent = saved ? button.dataset.labelSaved : labelAdd;
    };
    render(readWishlist().includes(handle));
    button.addEventListener('click', () => {
      const list = readWishlist();
      const saved = !list.includes(handle);
      const next = saved ? [...list, handle] : list.filter((item) => item !== handle);
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
      } catch {
        // Storage blocked (private mode): the button still toggles for this visit.
      }
      render(saved);
    });
  });
}

/**
 * Wishlist page (sections/wishlist.liquid): a card per saved product,
 * latest first, each rendered by sections/wishlist-card.liquid on the
 * product's URL. Products no longer online (404) are left out.
 */
class WishlistGrid extends HTMLElement {
  async connectedCallback() {
    const grid = this.querySelector('[data-wishlist-grid]');
    const root = this.dataset.root.replace(/\/$/, '');
    const cards = await Promise.all(
      readWishlist()
        .reverse()
        .map(async (handle) => {
          try {
            const response = await fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=wishlist-card`);
            if (!response.ok) return null;
            return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('.card');
          } catch {
            return null;
          }
        }),
    );
    grid.replaceChildren(...cards.filter(Boolean));
    grid.removeAttribute('aria-busy');
    this.querySelector('[data-wishlist-empty]').hidden = grid.children.length > 0;
  }
}

/**
 * Product gallery: a thumbnail brings its photo into the main frame and
 * the rest follow in order; the main image opens the full-screen gallery.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    // Fixed slots: the main image, then the stepped thumbnails (at most 4).
    this.slots = [
      this.querySelector('.product__main img'),
      ...this.querySelectorAll('.product__thumb img'),
    ].filter(Boolean);
    // Every image's sources, in product order; the ones past the fifth
    // live in [data-gallery-rest] until they rotate into a slot.
    this.images = [...this.slots, ...this.querySelectorAll('[data-gallery-rest] img')].map((img) =>
      Object.fromEntries(['srcset', 'src', 'width', 'height', 'alt'].map((name) => [name, img.getAttribute(name)])),
    );
    this.index = 0;
    this.current = this.querySelector('[data-current]');
    this.setupLightbox();

    this.querySelectorAll('.product__thumb').forEach((button, slot) => {
      button.addEventListener('click', () => this.show(this.index + slot + 1));
    });

    // Phones (EM_Web_WIP_04 pp. 39–41): chevrons beside the photo, or a
    // swipe across it, step through the photos.
    this.steps = this.querySelectorAll('[data-gallery-step]');
    this.steps.forEach((button) => {
      button.addEventListener('click', () => this.show(this.index + Number(button.dataset.galleryStep)));
    });
    this.updateSteps();
    const media = this.querySelector('.product__media');
    let startX = null;
    media?.addEventListener('touchstart', (event) => (startX = event.touches[0].clientX), { passive: true });
    media?.addEventListener('touchend', (event) => {
      if (startX === null || this.images.length < 2) return;
      const distance = event.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(distance) > 40) this.show(this.index + (distance < 0 ? 1 : -1));
    });
  }

  /**
   * Full-screen gallery (p. 30): the main image opens it on the current
   * photo; chevrons and the arrow keys walk through all of them; Close or
   * Escape (native to <dialog>) shut it, leaving the page on that photo.
   */
  setupLightbox() {
    this.lightbox = this.querySelector('[data-lightbox]');
    if (!this.lightbox) return;
    this.lightboxImage = this.lightbox.querySelector('[data-lightbox-image]');
    this.lightboxCurrent = this.lightbox.querySelector('[data-lightbox-current]');
    // The main image becomes the control that opens it (mouse and keyboard);
    // focus comes back to it when the gallery closes.
    const main = this.querySelector('.product__media');
    main.style.cursor = 'zoom-in';
    main.setAttribute('role', 'button');
    main.setAttribute('tabindex', '0');
    main.setAttribute('aria-label', this.lightbox.dataset.openLabel);
    const open = () => {
      this.lightboxShow(this.index);
      this.lightbox.showModal();
      this.lightbox.focus();
    };
    main.addEventListener('click', open);
    main.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      open();
    });
    this.lightbox.addEventListener('close', () => main.focus());
    this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
    this.lightbox.querySelectorAll('[data-lightbox-step]').forEach((button) => {
      button.addEventListener('click', () => this.lightboxShow(this.lightboxIndex + Number(button.dataset.lightboxStep)));
    });
    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.lightboxShow(this.lightboxIndex - 1);
      if (event.key === 'ArrowRight') this.lightboxShow(this.lightboxIndex + 1);
    });
    this.lightbox.addEventListener('close', () => this.show(this.lightboxIndex));
  }

  lightboxShow(index) {
    this.lightboxIndex = (index + this.images.length) % this.images.length;
    for (const [name, value] of Object.entries(this.images[this.lightboxIndex])) {
      value === null ? this.lightboxImage.removeAttribute(name) : this.lightboxImage.setAttribute(name, value);
    }
    if (this.lightboxCurrent) this.lightboxCurrent.textContent = this.lightboxIndex + 1;
  }

  /**
   * Show image `index` in the main frame and the ones after it, in order,
   * in the thumbnail slots, so every image is reachable however many there
   * are. Only sources move: each <img> keeps its own `sizes`, so the main
   * frame loads a large file and the quarters stay small.
   */
  show(index) {
    this.index = (index + this.images.length) % this.images.length;
    this.slots.forEach((img, slot) => {
      const image = this.images[(this.index + slot) % this.images.length];
      for (const [name, value] of Object.entries(image)) {
        value === null ? img.removeAttribute(name) : img.setAttribute(name, value);
      }
    });
    if (this.current) this.current.textContent = this.index + 1;
    this.updateSteps();
  }

  // No chevron before the first photo or after the last (pp. 39–41).
  updateSteps() {
    this.steps?.forEach((button) => {
      const step = Number(button.dataset.galleryStep);
      button.hidden = (step < 0 && this.index === 0) || (step > 0 && this.index === this.images.length - 1);
    });
  }
}

/**
 * Cart drawer (pp. 26–28). The header's Cart link opens it; Add to cart
 * posts to the cart API, swaps in the re-rendered section and opens it with
 * "N products added to your cart". Escape, the close button or a click
 * outside close it. If the API fails, the form submits normally (/cart).
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.toggle = document.querySelector('[data-cart-toggle]');
    this.toggle?.addEventListener('click', (event) => {
      event.preventDefault();
      this.hidden ? this.open() : this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });
    document.addEventListener('click', (event) => {
      if (!this.hidden && !this.contains(event.target) && !this.toggle?.contains(event.target)) this.close();
    });
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form.addEventListener('submit', (event) => this.add(event, form));
    });
  }

  open(title) {
    if (title) this.querySelector('.cart-drawer__title').textContent = title;
    this.hidden = false;
    this.toggle?.classList.add('is-active');
    this.querySelector('.cart-drawer__panel').focus();
  }

  close() {
    this.hidden = true;
    this.toggle?.classList.remove('is-active');
  }

  async add(event, form) {
    event.preventDefault();
    event.stopPropagation();
    const body = new FormData(form);
    body.append('sections', this.sectionId);
    body.append('sections_url', window.location.pathname);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      this.render(data.sections[this.sectionId]);
      const added = Number(body.get('quantity')) || 1;
      const title = this.querySelector('.cart-drawer__title');
      this.open(added === 1 ? title.dataset.addedOne : title.dataset.addedOther.replace('[count]', added));
    } catch {
      form.submit();
    }
  }

  /** Replace the drawer's contents with a freshly rendered section. */
  render(html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
    if (!fresh) return;
    // Move the parsed nodes across (they are adopted into this document).
    this.replaceChildren(...fresh.childNodes);
    this.dataset.itemCount = fresh.dataset.itemCount;
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = fresh.dataset.itemCount;
    });
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '') ?? 'cart-drawer';
  }
}

/**
 * Designers page (pp. 33–36): Order by (in the header's band) sorts the
 * list A–Z or Z–A.
 */
class DesignerList extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.designers__list');
    this.items = [...this.list.children];
    this.controls = document.querySelector('[data-designer-controls]') ?? this;
    this.controls.querySelectorAll('[data-sort]').forEach((button) => {
      button.addEventListener('click', () => this.sort(button));
    });

    // With a mouse, the last name pointed at keeps its portrait and bio on
    // screen until another name takes over.
    const hover = window.matchMedia('(hover: hover) and (min-width: 990px)');
    const activate = (event) => {
      const item = event.target.closest('.designers__item');
      if (!hover.matches || !item?.querySelector('.designers__detail')) return;
      this.items.forEach((other) => other.classList.toggle('is-active', other === item));
    };
    this.list.addEventListener('mouseover', activate);
    this.list.addEventListener('focusin', activate);

    // Phones and tablets (EM_Web_WIP_04 pp. 49–53): no hover, so a name
    // opens its portrait and bio under it like a drawer (and closes any
    // other), and closes it again; the portrait leads to the designer's
    // products and collections.
    const narrow = window.matchMedia('(max-width: 989px)');
    this.list.addEventListener('click', (event) => {
      const name = event.target.closest('.designers__name');
      const item = name?.closest('.designers__item');
      if (!narrow.matches || !item?.querySelector('.designers__detail')) return;
      event.preventDefault();
      const open = !item.classList.contains('is-open');
      this.items.forEach((other) => other.classList.toggle('is-open', open && other === item));
    });
  }

  sort(button) {
    const direction = button.dataset.sort === 'desc' ? -1 : 1;
    this.items.sort((a, b) => direction * a.dataset.name.localeCompare(b.dataset.name));
    this.list.append(...this.items);
    this.controls.querySelectorAll('[data-sort]').forEach((option) => {
      const selected = option === button;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-pressed', String(selected));
    });
  }
}

/**
 * About, "Our Designers": with data-autoscroll the row slides slowly to the
 * left in an endless loop. Two copies follow the images (hidden from
 * assistive tech), and the scroll jumps back by one set's width when it
 * runs past, which looks the same. Scrolling, swiping or dragging with a
 * mouse takes over; it starts again AUTOSCROLL_RESUME ms later. Off screen, in a
 * background tab or with reduced motion it stands still.
 */
const AUTOSCROLL_SPEED = 30; // px per second
const AUTOSCROLL_RESUME = 3000;

class ImageRow extends HTMLElement {
  connectedCallback() {
    const items = [...this.children];
    if (!this.hasAttribute('data-autoscroll') || items.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    for (let copy = 0; copy < 2; copy++) {
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        this.append(clone);
      });
    }
    const first = items[0];
    const firstClone = this.children[items.length];
    this.pausedUntil = 0;

    // One set's width; on a resize the row keeps its place in the set.
    const measure = () => {
      const loop = firstClone.offsetLeft - first.offsetLeft;
      if (loop === this.loop) return;
      const progress = this.loop ? (this.position - this.loop) / this.loop : 0;
      this.loop = loop;
      this.wrap(loop + progress * loop);
    };
    measure();
    new ResizeObserver(measure).observe(this);
    new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting)).observe(this);

    const pause = () => (this.pausedUntil = performance.now() + AUTOSCROLL_RESUME);
    ['wheel', 'keydown'].forEach((type) => this.addEventListener(type, pause, { passive: true }));
    // A finger on the row holds it; the pause counts from when it lifts.
    this.addEventListener('touchstart', () => (this.touching = true), { passive: true });
    ['touchend', 'touchcancel'].forEach((type) =>
      this.addEventListener(type, () => {
        this.touching = false;
        pause();
      }, { passive: true }),
    );
    // Manual scrolling (and its momentum) keeps the pause going. The jump
    // back by a set waits until the scroll stops: moving it mid-gesture
    // breaks the swipe on phones.
    let settle;
    this.addEventListener('scroll', () => {
      if (performance.now() >= this.pausedUntil && !this.touching) return;
      pause();
      clearTimeout(settle);
      settle = setTimeout(() => {
        if (!this.touching) this.wrap(this.scrollLeft);
      }, 150);
    }, { passive: true });
    this.dragWithMouse(pause);

    let last = performance.now();
    const step = (now) => {
      const elapsed = Math.min(now - last, 100);
      last = now;
      if (this.visible && !document.hidden && !this.touching && now >= this.pausedUntil) {
        this.wrap(this.position + (AUTOSCROLL_SPEED * elapsed) / 1000);
      } else {
        this.position = this.scrollLeft;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /** Keeps the scroll within the middle copy. */
  wrap(position) {
    if (position >= this.loop * 2) position -= this.loop;
    if (position < this.loop) position += this.loop;
    this.position = position;
    if (Math.abs(this.scrollLeft - position) >= 0.5) this.scrollLeft = position;
  }

  /** Mouse drag scrolls the row; a drag is not a click on the image. */
  dragWithMouse(pause) {
    let startX = 0;
    let startScroll = 0;
    let dragged = false;
    this.addEventListener('pointerdown', (event) => {
      pause();
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      startX = event.clientX;
      startScroll = this.scrollLeft;
      dragged = false;
      this.setPointerCapture(event.pointerId);
    });
    this.addEventListener('pointermove', (event) => {
      if (!this.hasPointerCapture(event.pointerId)) return;
      const distance = event.clientX - startX;
      if (Math.abs(distance) > 5) dragged = true;
      pause();
      this.wrap(startScroll - distance);
      startScroll = this.scrollLeft + distance;
    });
    this.addEventListener('pointerup', (event) => this.releasePointerCapture(event.pointerId));
    this.addEventListener('click', (event) => {
      if (!dragged) return;
      event.preventDefault();
      dragged = false;
    }, true);
  }
}

/**
 * Product listing: "See more" instead of page numbers. Fetches the next
 * page and appends its cards to the grid; without JavaScript the link just
 * opens that page. Phones show data-step cards at a time (12): the ones
 * already loaded first, then the next page.
 */
class SeeMore extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a');
    this.grid = this.parentElement.querySelector('.product-grid');
    this.hasNext = this.hasAttribute('data-next');
    this.step = Number(this.dataset.step) || Infinity;
    this.phone = window.matchMedia('(max-width: 749px)');
    if (this.phone.matches) this.hideFrom([...this.grid.children]);
    this.phone.addEventListener('change', () => {
      if (!this.phone.matches) this.waiting().forEach((card) => (card.hidden = false));
      this.update();
    });
    this.update();
    this.link.addEventListener('click', (event) => {
      event.preventDefault();
      this.load();
    });
  }

  waiting() {
    return [...this.grid.children].filter((card) => card.hidden);
  }

  /** On phones, every card after the first step waits. */
  hideFrom(cards) {
    if (this.phone.matches) cards.slice(this.step).forEach((card) => (card.hidden = true));
  }

  update() {
    this.hidden = !this.hasNext && !this.waiting().length;
  }

  async load() {
    const waiting = this.waiting();
    if (waiting.length) {
      waiting.slice(0, this.step).forEach((card) => (card.hidden = false));
      this.update();
      return;
    }
    if (this.loading || !this.hasNext) return;
    this.loading = true;
    try {
      const response = await fetch(this.link.href);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = [...html.querySelector('.product-grid').children];
      this.hideFrom(cards);
      this.grid.append(...cards);
      const next = html.querySelector('see-more[data-next] a');
      if (next) this.link.href = next.href;
      else this.hasNext = false;
      this.update();
    } catch {
      window.location.href = this.link.href;
    } finally {
      this.loading = false;
    }
  }
}

/**
 * Stockists page (EM_Web_WIP_05 pp. 25–26). From 990 px it loads MapLibre
 * (assets/maplibre-gl.js, only here) and draws a globe from OpenFreeMap's
 * Positron style in the layout's two greys, with a point per shop. The
 * globe opens on the visitor's country (Shopify's IP-based suggestion).
 * Choosing a shop, in the list or on the globe, flies to it at street
 * level and fills the bars: local time (ticking), weather and temperature
 * from Open-Meteo above, name and address below. Choosing it again, or
 * Escape, goes back to the whole globe. Narrower, or if the map can't
 * load, the shops stay plain links to Google Maps.
 */
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';
const WEATHER_API = 'https://api.open-meteo.com/v1/forecast';
const STREET_ZOOM = 16;
// Editions Milano, Via Solferino 46: the globe's centre when the visitor's
// country has no shops and can't be found on the map.
const HOME = [9.1863, 45.4776];

class StockistLocator extends HTMLElement {
  connectedCallback() {
    this.shops = [...this.querySelectorAll('[data-stockist]')];
    this.panel = this.querySelector('[data-map]');
    this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
    const desktop = window.matchMedia('(min-width: 990px)');
    const start = () => {
      if (!desktop.matches || this.loading || !this.shops.length) return;
      this.loading = this.setup().catch(() => {
        this.map?.remove();
        this.map = null;
        this.panel.hidden = true;
      });
    };
    start();
    desktop.addEventListener('change', start);
    this.addEventListener('click', (event) => {
      const shop = event.target.closest('[data-stockist]');
      if (!shop || !this.map || !desktop.matches) return;
      event.preventDefault();
      this.select(shop === this.selected ? null : shop);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.selected) this.select(null);
    });
    this.setupDrawers();
  }

  /**
   * Phones and tablets (no globe): only the countries show, each a button
   * that opens its shops below it like a drawer; several can be open.
   */
  setupDrawers() {
    const compact = window.matchMedia('(max-width: 989px)');
    const toggles = [...this.querySelectorAll('.stockists__country-toggle')];
    const apply = () => {
      this.classList.toggle('is-compact', compact.matches);
      toggles.forEach((toggle) => {
        if (compact.matches) {
          toggle.setAttribute('role', 'button');
          toggle.tabIndex = 0;
          toggle.setAttribute('aria-expanded', String(toggle.closest('.stockists__country').classList.contains('is-open')));
        } else {
          ['role', 'tabindex', 'aria-expanded'].forEach((name) => toggle.removeAttribute(name));
        }
      });
    };
    const toggle = (button) => {
      const open = button.closest('.stockists__country').classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(open));
    };
    apply();
    compact.addEventListener('change', apply);
    this.addEventListener('click', (event) => {
      const button = event.target.closest('.stockists__country-toggle');
      if (button && compact.matches) toggle(button);
    });
    this.addEventListener('keydown', (event) => {
      const button = event.target.closest?.('.stockists__country-toggle[role="button"]');
      if (!button || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      toggle(button);
    });
  }

  static loadLibrary(src, href) {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    document.head.append(Object.assign(document.createElement('link'), { rel: 'stylesheet', href }));
    return new Promise((resolve, reject) => {
      const script = Object.assign(document.createElement('script'), { src, async: true });
      script.onload = () => resolve(window.maplibregl);
      script.onerror = reject;
      document.head.append(script);
    });
  }

  async setup() {
    const [maplibregl, style, country] = await Promise.all([
      StockistLocator.loadLibrary(this.dataset.library, this.dataset.stylesheet),
      fetch(MAP_STYLE).then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json();
      }),
      this.visitorCountry(),
    ]);
    this.restyle(style);
    this.panel.hidden = false;
    this.globe = this.querySelector('[data-globe]');
    // The map draws in a box as tall as the globe's room with the header
    // shown (.stockists__room), centred in it. Hiding the header on scroll
    // only adds white around the box: a taller canvas would show the globe
    // bigger, even at the same zoom (the camera's perspective follows it).
    this.room = document.createElement('div');
    this.room.className = 'stockists__room';
    this.globe.append(this.room);
    this.map = new maplibregl.Map({
      container: this.room,
      style,
      center: this.centreOf(country) ?? HOME,
      zoom: 2,
      renderWorldCopies: false,
      dragRotate: false,
      touchPitch: false,
      maxPitch: 0,
      // The wheel (and a trackpad pinch, sent as Ctrl + wheel) never zooms:
      // the page scrolls on, and a hint says what the map does instead.
      scrollZoom: false,
      attributionControl: { compact: true },
    });
    await new Promise((resolve) => this.map.once('load', resolve));

    // Credits start folded behind their "i" (MapLibre opens them at first).
    const credits = this.globe.querySelector('.maplibregl-ctrl-attrib');
    credits?.classList.remove('maplibregl-compact-show');
    credits?.setAttribute('open', '');

    const hint = document.createElement('p');
    hint.className = 'stockists__hint';
    hint.setAttribute('aria-hidden', 'true');
    hint.textContent = this.strings.hint;
    this.globe.append(hint);
    let hintTimer;
    this.globe.addEventListener('wheel', (event) => {
      // A pinch would zoom the whole page instead: not over the map.
      if (event.ctrlKey) event.preventDefault();
      hint.classList.add('is-visible');
      clearTimeout(hintTimer);
      hintTimer = setTimeout(() => hint.classList.remove('is-visible'), 1500);
    }, { passive: false });

    this.points = new Map();
    for (const shop of this.shops) {
      const point = document.createElement('span');
      point.className = 'stockists__point';
      point.addEventListener('click', (event) => {
        event.stopPropagation();
        this.select(shop === this.selected ? null : shop);
      });
      new maplibregl.Marker({ element: point, opacityWhenCovered: '0' })
        .setLngLat([Number(shop.dataset.lng), Number(shop.dataset.lat)])
        .addTo(this.map);
      // The list is the accessible control; the points only repeat it.
      point.setAttribute('aria-hidden', 'true');
      point.removeAttribute('tabindex');
      this.points.set(shop, point);
    }

    this.fit();
    this.map.on('move', () => this.drawEdge());
    new ResizeObserver(() => {
      if (!this.selected) this.fit();
      this.drawEdge();
    }).observe(this.room);
    if (!this.centreOf(country)) this.map.once('idle', () => this.findCountry(country));
  }

  /**
   * The layout's globe: land #F5F5F5, sea #C9C9C9, nothing else until
   * street level (names, borders, parks and land cover from zoom 8).
   */
  restyle(style) {
    const tokens = getComputedStyle(this);
    const land = tokens.getPropertyValue('--globe-land').trim();
    const water = tokens.getPropertyValue('--globe-water').trim();
    style.projection = { type: 'globe' };
    style.sky = { 'atmosphere-blend': 0 };
    for (const layer of style.layers) {
      if (layer.id === 'background') {
        layer.paint['background-color'] = land;
      } else if (layer.type === 'symbol' || ['boundary', 'park', 'landcover', 'landuse'].includes(layer['source-layer'])) {
        layer.minzoom = Math.max(layer.minzoom ?? 0, 8);
      } else if (layer['source-layer'] === 'water' || layer['source-layer'] === 'waterway') {
        layer.paint[`${layer.type}-color`] = water;
      }
    }
  }

  /** ISO code of the visitor's country: Shopify's IP-based suggestion, else the store's market. */
  async visitorCountry() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}browsing_context_suggestions.json`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      return data.detected_values?.country?.handle || this.dataset.country;
    } catch {
      return this.dataset.country;
    }
  }

  /** The middle of a country's shops, if it has any. */
  centreOf(country) {
    const shops = this.shops.filter((shop) => shop.dataset.countryCode === country);
    if (!shops.length) return null;
    const mean = (key) => shops.reduce((sum, shop) => sum + Number(shop.dataset[key]), 0) / shops.length;
    return [mean('lng'), mean('lat')];
  }

  /** A country without shops: turn to its label point in the map data, if it's there. */
  findCountry(country) {
    if (!country || this.selected) return;
    const [place] = this.map.querySourceFeatures('openmaptiles', {
      sourceLayer: 'place',
      filter: ['==', ['get', 'iso_a2'], country],
    });
    if (place?.geometry.type === 'Point') this.map.easeTo({ center: place.geometry.coordinates });
  }

  /**
   * On screen the globe's edge is where points furthest from the centre
   * stop moving outwards (perspective hides the last few degrees), so the
   * radius is the largest projected distance along one meridian.
   */
  globeRadius() {
    const centre = this.map.getCenter();
    const middle = this.map.project(centre);
    const towards = centre.lat > 0 ? -1 : 1;
    let radius = 0;
    for (let degrees = 45; degrees <= 90; degrees += 1) {
      const edge = this.map.project([centre.lng, centre.lat + towards * degrees]);
      radius = Math.max(radius, Math.hypot(edge.x - middle.x, edge.y - middle.y));
    }
    return Number.isFinite(radius) ? radius : 0;
  }

  /** Zoom at which the globe just fills the room between the bars (p. 25). */
  fit() {
    const target = Math.min(this.room.clientWidth, this.room.clientHeight) - 2;
    for (let step = 0; step < 3; step += 1) {
      const radius = this.globeRadius();
      if (!radius) break;
      this.map.jumpTo({ zoom: this.map.getZoom() + Math.log2(target / (2 * radius)) });
    }
    this.fitZoom = this.map.getZoom();
    this.drawEdge();
  }

  drawEdge() {
    const size = this.map.getZoom() > 6 ? 0 : 2 * this.globeRadius();
    this.globe.style.setProperty('--globe-edge', `${size}px`);
  }

  select(shop) {
    this.selected?.classList.remove('is-selected');
    this.points.get(this.selected)?.classList.remove('is-selected');
    clearInterval(this.clock);
    this.selected = shop;
    this.classList.toggle('has-selection', Boolean(shop));
    if (!shop) {
      this.map.flyTo({ zoom: this.fitZoom, essential: true });
      return;
    }
    shop.classList.add('is-selected');
    this.points.get(shop).classList.add('is-selected');
    this.querySelector('[data-selected]').replaceChildren(...shop.cloneNode(true).childNodes);
    this.map.flyTo({ center: [Number(shop.dataset.lng), Number(shop.dataset.lat)], zoom: STREET_ZOOM, essential: true });
    this.showConditions(shop);
  }

  /** Local time, weather and temperature where the shop is. */
  async showConditions(shop) {
    const time = this.querySelector('[data-time]');
    const weather = this.querySelector('[data-weather]');
    const temperature = this.querySelector('[data-temperature]');
    [time, weather, temperature].forEach((value) => (value.textContent = '–'));
    try {
      const query = new URLSearchParams({
        latitude: shop.dataset.lat,
        longitude: shop.dataset.lng,
        current: 'temperature_2m,weather_code',
        timezone: 'auto',
      });
      const response = await fetch(`${WEATHER_API}?${query}`);
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      if (shop !== this.selected) return;
      const clock = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: data.timezone,
      });
      const tick = () => (time.textContent = clock.format(new Date()));
      tick();
      this.clock = setInterval(tick, 1000);
      const celsius = data.current.temperature_2m;
      temperature.textContent = `${Math.round(celsius)}° C / ${Math.round((celsius * 9) / 5 + 32)}° F`;
      weather.textContent = this.strings.conditions[StockistLocator.condition(data.current.weather_code)] ?? '–';
    } catch {
      // Leave the dashes: the shop and the map still work.
    }
  }

  /** WMO weather code (Open-Meteo) to one of the page's ten wordings. */
  static condition(code) {
    if (code === 0) return 'clear';
    if (code === 1) return 'mostly_clear';
    if (code === 2) return 'partly_cloudy';
    if (code === 3) return 'overcast';
    if (code <= 48) return 'fog';
    if (code <= 57) return 'drizzle';
    if (code <= 67) return 'rain';
    if (code <= 77 || code === 85 || code === 86) return 'snow';
    if (code <= 82) return 'showers';
    return 'thunderstorm';
  }
}

/**
 * Header (EM_Web_WIP_05 pp. 2–11). Scrolling down slides the top row away
 * and keeps the category band (html.header-hidden); scrolling up, or the
 * pointer at the top of the window, brings it back. Never while one of
 * its panels or the cart is open. html.at-top marks the top of the page,
 * where the home page hides its band (set early in layout/theme.liquid).
 */
function setupHeader() {
  const root = document.documentElement;
  const group = document.querySelector('.shopify-section-group-header-group');
  if (!group) return;
  const row = group.querySelector('.header__bar');
  let last = window.scrollY;
  let queued = false;
  const busy = () => group.querySelector('details[open]') || document.querySelector('cart-drawer:not([hidden])');
  const update = () => {
    queued = false;
    const y = window.scrollY;
    root.classList.toggle('at-top', y <= 0);
    if (y <= row.offsetHeight || busy()) root.classList.remove('header-hidden');
    else if (y > last + 4) root.classList.add('header-hidden');
    else if (y < last - 4) root.classList.remove('header-hidden');
    last = y;
  };
  update();
  window.addEventListener(
    'scroll',
    () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    },
    { passive: true },
  );
  document.addEventListener('mousemove', (event) => {
    if (event.clientY <= row.offsetHeight) root.classList.remove('header-hidden');
  });
}

/**
 * Panels (menu, search bar, order by) are <details data-panel>:
 * close with Escape or a click outside; a search field takes focus when
 * its panel opens. Menu and search are exclusive via name="header-panel".
 */
function setupPanels() {
  const panels = document.querySelectorAll('[data-panel]');
  panels.forEach((panel) => {
    panel.addEventListener('toggle', () => {
      if (panel.open) panel.querySelector('input[type="search"]')?.focus();
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    panels.forEach((panel) => {
      if (!panel.open) return;
      panel.open = false;
      panel.querySelector('summary')?.focus();
    });
  });
  document.addEventListener('click', (event) => {
    panels.forEach((panel) => {
      if (panel.open && !panel.contains(event.target)) panel.open = false;
    });
  });

  // Phones: Search, first in the menu, opens the search bar in its place
  // (the link to the search page stays for tablets, where the bar has its
  // own toggle). While the bar is open the menu's cross closes it.
  const search = document.querySelector('.header__search');
  if (!search) return;
  document.querySelector('[data-search-open]')?.addEventListener('click', (event) => {
    if (search.querySelector('summary').offsetParent !== null) return;
    event.preventDefault();
    event.stopPropagation();
    search.open = true;
  });
  document.querySelector('[data-menu-toggle]')?.addEventListener('click', (event) => {
    if (!search.open) return;
    event.preventDefault();
    search.open = false;
  });
}

/**
 * Order by on the listing: an open group of values (Designers, Collection,
 * Color) lies under the options row, so the grid moves down by both.
 * Phones list the values in place and need nothing.
 *
 * Sorts, values and the × don't reload the page, and neither do the
 * band's categories between listings: the listing and the header come back
 * from the Section Rendering API for the link's URL, the grid, the Order
 * by options and the band's links are swapped in (the panel and the open
 * group stay open), the title follows and the URL is pushed, so Back and
 * Forward step through them too. If the request fails, or the page isn't
 * a listing, the link loads as a page.
 */
function setupOrderBy() {
  const orderBy = document.querySelector('.order-by');
  const listing = document.querySelector('.product-grid')?.closest('.shopify-section');
  if (!orderBy) return;
  const header = orderBy.closest('.shopify-section');
  const panel = orderBy.querySelector('.order-by__panel');
  const update = () => {
    const values = orderBy.open && orderBy.querySelector('[data-order-group][open] .order-by__values');
    if (values && getComputedStyle(values).position === 'absolute') {
      document.body.style.setProperty('--order-by-height', `${panel.offsetHeight + values.offsetHeight}px`);
    } else {
      document.body.style.removeProperty('--order-by-height');
    }
  };
  const watchGroups = () => {
    orderBy.querySelectorAll('[data-order-group]').forEach((group) => group.addEventListener('toggle', update));
  };
  orderBy.addEventListener('toggle', update);
  watchGroups();
  window.addEventListener('resize', update);
  if (!listing || !header) return;

  const id = (section) => section.id.replace('shopify-section-', '');
  let request = null;
  const load = async (url, push) => {
    request?.abort();
    request = new AbortController();
    const query = new URL(url, window.location.href);
    query.searchParams.set('sections', `${id(listing)},${id(header)}`);
    listing.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(query, { signal: request.signal });
      if (!response.ok) throw new Error(response.statusText);
      const sections = await response.json();
      const parse = (key) => new DOMParser().parseFromString(sections[key], 'text/html');
      const nextHeader = parse(id(header));
      const nextOrderBy = nextHeader.querySelector('.order-by');
      const nextListing = parse(id(listing)).querySelector('.shopify-section');
      if (!nextOrderBy || !nextListing) throw new Error('not a listing');
      const open = orderBy.querySelector('[data-order-group][open]')?.dataset.orderGroup;
      const moved = new URL(url, window.location.href).pathname !== window.location.pathname;
      listing.innerHTML = nextListing.innerHTML;
      orderBy.querySelector('summary').innerHTML = nextOrderBy.querySelector('summary').innerHTML;
      panel.innerHTML = nextOrderBy.querySelector('.order-by__panel').innerHTML;
      const reopen = open && panel.querySelector(`[data-order-group="${open}"]`);
      if (reopen) reopen.open = true;
      // The band's links: which category is current.
      ['.header__categories', '.band-menu__panel'].forEach((selector) => {
        const next = nextHeader.querySelectorAll(selector);
        header.querySelectorAll(selector).forEach((list, index) => {
          if (next[index]) list.innerHTML = next[index].innerHTML;
        });
      });
      const title = listing.querySelector('h1')?.textContent.trim();
      if (title) document.title = `${title} – ${document.title.split(' – ').pop()}`;
      watchGroups();
      update();
      if (push) window.history.pushState({ orderBy: true }, '', url);
      if (moved) window.scrollTo(0, 0);
      listing.removeAttribute('aria-busy');
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = url;
    }
  };

  window.history.replaceState({ ...window.history.state, orderBy: true }, '');
  const follow = (event, link) => {
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    load(link.href, true);
  };
  orderBy.addEventListener('click', (event) => follow(event, event.target.closest('a[href]')));
  // The band's categories (and on phones its Collections, Designers and
  // Explore lists): only links to another listing, and the phone's list
  // closes as the link is followed.
  header.querySelectorAll('.header__categories, .band-menu').forEach((nav) => {
    nav.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || !new URL(link.href).pathname.includes('/collections/')) return;
      follow(event, link);
      link.closest('details')?.removeAttribute('open');
    });
  });
  // Only the entries this pushed (the popups' #links make their own).
  window.addEventListener('popstate', (event) => {
    if (event.state?.orderBy) load(window.location.href, false);
  });
}

/**
 * Newsletter / Catalogue panels (snippets/popups.liquid). Opened by links to
 * #<id>, by the footer's Subscribe (newsletter), or by themselves after
 * data-delay seconds. Closing keeps them from opening by themselves for
 * data-frequency days; a sign-up or a download, for good. One at a time.
 * Storage can fail (private mode): then they simply behave as first visits.
 */
class PopupPanel extends HTMLElement {
  static store(key, value) {
    try {
      if (value === undefined) return localStorage.getItem(key);
      localStorage.setItem(key, value);
    } catch {
      return null;
    }
    return null;
  }

  connectedCallback() {
    this.key = `em-popup-${this.id}`;
    this.panel = this.querySelector('.popup__panel');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-popup-close]')) this.close();
      if (event.target.closest('[data-popup-done-on-click]')) this.done();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });
    document.addEventListener('click', (event) => {
      const link = event.target.closest(`a[href$="#${this.id}"], [data-popup="${this.id}"]`);
      if (!link) return;
      event.preventDefault();
      this.open();
    });

    // Back from a sign-up (thanks or errors): show it straight away.
    if (this.querySelector('[data-open-now]')) {
      if (this.querySelector('[data-popup-done]')) this.done();
      this.open();
      return;
    }
    if (window.location.hash === `#${this.id}`) {
      this.open();
      return;
    }
    const delay = Number(this.dataset.delay) || 0;
    if (delay > 0 && this.due()) setTimeout(() => this.autoOpen(), delay * 1000);
  }

  /** Not dismissed within the last data-frequency days, and not done. */
  due() {
    const saved = PopupPanel.store(this.key);
    if (saved === 'done') return false;
    const days = Number(this.dataset.frequency) || 7;
    return !saved || Date.now() - Number(saved) > days * 86400000;
  }

  autoOpen() {
    // Never on top of another panel, the cart or the cookie banner (also a
    // green panel on the left); try again a bit later.
    const banner = document.getElementById('shopify-pc__banner');
    const busy =
      [...document.querySelectorAll('popup-panel, cart-drawer')].some((el) => el !== this && !el.hidden) ||
      (banner && banner.getClientRects().length > 0);
    if (busy) {
      setTimeout(() => this.autoOpen(), 5000);
      return;
    }
    if (this.due()) this.open();
  }

  open() {
    document.querySelectorAll('popup-panel').forEach((el) => el !== this && !el.hidden && el.close());
    this.hidden = false;
    (this.querySelector('input:not([type="hidden"])') ?? this.panel).focus({ preventScroll: true });
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    if (PopupPanel.store(this.key) !== 'done') PopupPanel.store(this.key, String(Date.now()));
  }

  done() {
    PopupPanel.store(this.key, 'done');
  }
}

/**
 * "Cookie preferences" in the footer (Cookie Policy §5) reopens the
 * preferences of Shopify's cookie banner (Customer Privacy API,
 * privacyBanner.showPreferences). The item stays hidden where the banner
 * isn't loaded: banner off in Settings > Customer privacy, or a region
 * without it.
 */
function setupCookiePreferences() {
  const item = document.querySelector('[data-cookie-preferences]');
  if (!item) return;
  const reveal = () => {
    if (typeof window.privacyBanner?.showPreferences !== 'function') return false;
    item.hidden = false;
    item.querySelector('button').addEventListener('click', () => window.privacyBanner.showPreferences());
    return true;
  };
  // The banner script arrives async with content_for_header.
  if (!reveal()) window.addEventListener('load', reveal, { once: true });
}

/**
 * Trade form, "Add me to the newsletter" ticked: first the hidden customer
 * form (sections/trade.liquid) signs the visitor up, tagged newsletter +
 * trade, into a hidden frame; then the registration goes, its email saying
 * whether the sign-up went through, so the team knows when to subscribe
 * someone by hand. Shopify lands a good sign-up on ?customer_posted=true; a
 * captcha challenge, an error or no answer within 10 s count as not
 * confirmed. Both go through requestSubmit(), so Shopify's captcha handles
 * them as usual.
 */
function setupTradeNewsletter() {
  const form = document.getElementById('trade-form');
  const box = form?.querySelector('[data-trade-newsletter]');
  const status = form?.querySelector('[data-trade-newsletter-status]');
  const signup = document.getElementById('trade-newsletter-form');
  const frame = signup && document.querySelector(`iframe[name="${signup.target}"]`);
  if (!box || !status || !frame) return;

  const subscribe = () =>
    new Promise((resolve) => {
      const timer = setTimeout(() => resolve(false), 10000);
      frame.addEventListener(
        'load',
        () => {
          clearTimeout(timer);
          try {
            resolve(new URL(frame.contentWindow.location.href).searchParams.get('customer_posted') === 'true');
          } catch {
            resolve(false);
          }
        },
        { once: true }
      );
      signup.requestSubmit();
    });

  let state = 'idle'; // then 'subscribing', then 'done'
  form.addEventListener('submit', async (event) => {
    if (!box.checked || state === 'done') return;
    event.preventDefault();
    if (state === 'subscribing') return;
    state = 'subscribing';
    const value = (name) => form.elements.namedItem(`contact[${name}]`)?.value ?? '';
    signup.elements.namedItem('contact[email]').value = value('email');
    signup.elements.namedItem('contact[first_name]').value = value('First name');
    signup.elements.namedItem('contact[last_name]').value = value('Last name');
    signup.elements.namedItem('contact[note][Company]').value = value('Company');
    const subscribed = await subscribe();
    status.name = 'contact[Newsletter in Shopify]';
    status.value = subscribed ? 'Subscribed (tags newsletter, trade)' : 'Not confirmed: subscribe by hand';
    state = 'done';
    form.requestSubmit();
  });
}

/**
 * Consent withdrawn (banner or "Cookie preferences"): Google's tag stops
 * loading, but the cookies it set would stay for up to two years. Analytics
 * denied: delete _ga and _ga_*; marketing denied: delete _gcl_*. Google sets
 * them on the parent domain, so each is expired on the host and on every
 * parent (a public suffix just refuses the write).
 */
function setupConsentCleanup() {
  const domains = () => {
    const parts = window.location.hostname.split('.');
    return parts.slice(0, -1).map((_, index) => parts.slice(index).join('.'));
  };
  const expire = (test) => {
    document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter(test)
      .forEach((name) => {
        document.cookie = `${name}=; Max-Age=0; path=/`;
        domains().forEach((domain) => {
          document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
        });
      });
  };
  document.addEventListener('visitorConsentCollected', (event) => {
    const consent = event.detail ?? {};
    if (consent.analyticsAllowed === false) expire((name) => name === '_ga' || name.startsWith('_ga_'));
    if (consent.marketingAllowed === false) expire((name) => name.startsWith('_gcl_'));
  });
}

customElements.define('quantity-input', QuantityInput);
customElements.define('product-gallery', ProductGallery);
customElements.define('cart-drawer', CartDrawer);
customElements.define('designer-list', DesignerList);
customElements.define('see-more', SeeMore);
customElements.define('image-row', ImageRow);
customElements.define('stockist-locator', StockistLocator);
customElements.define('popup-panel', PopupPanel);
customElements.define('wishlist-grid', WishlistGrid);
// iOS Safari only applies :active (the black "pressed" buttons) when the
// page listens for touches; hover effects are off on touch screens.
document.addEventListener('touchstart', () => {}, { passive: true });
setupPanels();
setupHeader();
setupOrderBy();
// Home hero video: only the one on screen plays (the wide one, or the
// upright one on phones); the other pauses and stops preloading. Still when
// the visitor asks for reduced motion.
function setupHeroVideos() {
  const videos = document.querySelectorAll('.hero__video');
  if (!videos.length) return;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const update = () => {
    videos.forEach((video) => {
      const shown = getComputedStyle(video).display !== 'none';
      if (shown && !still) {
        video.preload = 'auto';
        video.play().catch(() => {});
      } else {
        video.removeAttribute('autoplay');
        if (!shown) video.preload = 'none';
        video.pause();
      }
    });
  };
  update();
  window.matchMedia('(max-width: 749px)').addEventListener('change', update);
}
setupHeroVideos();
setupWishlist();
setupMoreInfo();
setupCookiePreferences();
setupConsentCleanup();
setupTradeNewsletter();
